import { CalendarProviderSchema, type CalendarProvider } from '@gap/backend/app-services/calendar/models/CalendarEnums'
import { Timeouts } from '@gap/utils/date-utils'

/** Stash keys the connect flow writes and the OAuth callback reads (provider isn't in the callback URL). */
export const CALENDAR_OAUTH_PROVIDER_KEY = 'calendar_oauth_provider'
export const CALENDAR_OAUTH_RETURN_PATH_KEY = 'calendar_oauth_return_path'
/** Set by the callback, read by the Calendar tab so "Calendar connected" lands where the user can see it. */
export const CALENDAR_OAUTH_CONNECTED_KEY = 'calendar_oauth_connected_at'

/** Beyond this the stash is treated as abandoned, so a later visit can't replay a stale success. */
const CONNECTED_ANNOUNCEMENT_MAX_AGE_MS = Timeouts.minute

/**
 * True when the callback carried a `state` at all. The value is an opaque single-use token the
 * backend issued and is the only party that can judge, so there is no shape to check here — this
 * only catches a callback that arrived without one, which can never complete.
 */
export function hasState(state: string | null): boolean {
   return state !== null && state.length > 0
}

/** Type guard: is this string a supported calendar provider. */
export function isValidProvider(provider: string | null): provider is CalendarProvider {
   return provider !== null && (CalendarProviderSchema.options as readonly string[]).includes(provider)
}

/** Record a completed connect for the Calendar tab to announce once the user lands on it. */
export function stashConnectedAnnouncement(): void {
   sessionStorage.setItem(CALENDAR_OAUTH_CONNECTED_KEY, String(Date.now()))
}

/** Consume a pending connect announcement. True at most once, and only while it is still fresh. */
export function consumeConnectedAnnouncement(): boolean {
   const stashedAt = sessionStorage.getItem(CALENDAR_OAUTH_CONNECTED_KEY)
   sessionStorage.removeItem(CALENDAR_OAUTH_CONNECTED_KEY)
   if (!stashedAt) return false
   const age = Date.now() - Number(stashedAt)
   return Number.isFinite(age) && age >= 0 && age <= CONNECTED_ANNOUNCEMENT_MAX_AGE_MS
}

/**
 * Microsoft codes meaning the tenant blocks the user from granting these scopes themselves:
 * 65001 (nobody has consented yet) and 90094 (the grant needs an admin). Retrying cannot clear
 * either, so the user has to be told to go to their admin instead.
 */
const ADMIN_APPROVAL_ERROR_CODES = ['AADSTS65001', 'AADSTS90094']

/** True when the provider refused because only an admin can approve the app for this tenant. */
export function requiresAdminApproval(errorDescription: string | null): boolean {
   if (!errorDescription) return false
   return ADMIN_APPROVAL_ERROR_CODES.some(code => errorDescription.includes(code))
}
